/**
 * PDP FEATURE_SHOWCASE PL widget — NON-DS / legacy path. Renders the shared `FeatureSectionTwo` atom
 * (image/video + icon/title/subtitle grid), the same one the legacy PDP `FEATURE_SECTION_2` uses. The
 * atom is responsive on its own (image-left grid on desktop, image-top grid on mobile), so one widget
 * covers both dweb + mweb. Widget stays a thin view; the data→props mapping lives in the adapter.
 */

import { useTranslation } from 'next-i18next'

import { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import {
  getFeatureSectionTwoAnalytics,
  useFeatureSectionTwoImpressionRef,
} from '@src/components/widgets/use-ia-impression'
import { FeatureSectionTwo } from '@src/quince-components/src'

import { adaptFeatureShowcase } from './feature-showcase.adapter'
import styles from './feature-showcase.module.scss'

import type { FeatureShowcaseWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

export function FeatureShowcaseWidgetLegacy(props: PLTypedWidgetProps<FeatureShowcaseWidgetData>) {
  const { t } = useTranslation('common')
  const data = props.data.featureShowcaseData
  // FS2 `feature_section_two_viewed` impression on scroll-into-view (IA-only), mirroring non-PL
  // `image-with-text-product-details-section-legacy`. Hook is called unconditionally (Rules of Hooks);
  // the ref only wraps rendered output, so an empty section (null return below) never fires.
  const impressionRef = useFeatureSectionTwoImpressionRef(getFeatureSectionTwoAnalytics(data))
  if (!data?.media || data.items.length === 0) return null

  const { imageProps, videoProps, contents } = adaptFeatureShowcase(data)

  return (
    <div ref={impressionRef}>
      <FeatureSectionTwo
        heading={t('feature_section')}
        imageProps={imageProps}
        videoProps={videoProps}
        contents={contents}
        // Make the hero (image or video) fill the row height (no top/bottom gap) and vertically center
        // the icon grid beside it — see feature-showcase.module.scss.
        className={styles.media}
      />
    </div>
  )
}
